package org.teddapp.project.ui.objectives

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Objective
import kotlinx.coroutines.launch
import androidx.compose.runtime.LaunchedEffect
import org.teddapp.project.core.CoreFacts
import org.teddapp.project.core.TeddIdentity
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddDateField
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/**
 * Échéance passée sans que l'objectif soit atteint : aucun rouge, aucun « en retard ».
 * Un écran calme pose une question, la réflexion est écrite et numérotée (« tentative n°2 »),
 * l'objectif reste ouvert et peut recevoir une nouvelle échéance.
 */
@Composable
internal fun ObjectivePastDeadlineBlock(objective: Objective, repos: TeddRepositories) {
    val deadline = objective.deadline
    if (deadline.isNullOrBlank() || objective.status == "done" || !isOverdue(deadline)) return
    val reflections by repos.reflections.observeByObjective(objective.id).collectAsState(emptyList())
    val core = LocalCoreSession.current
    val scope = rememberCoroutineScope()
    // L'échéance passée est un fait : le noyau répond par une phrase sans pénalité.
    LaunchedEffect(objective.id, deadline) {
        core?.record(TeddIdentity.USER_ID, CoreFacts.goalMissed(objective.id, objective.title))
    }
    var text by remember(objective.id) { mutableStateOf("") }
    var pickingDeadline by remember(objective.id) { mutableStateOf(false) }
    val attempt = (reflections.maxOfOrNull { it.attempt } ?: 0L) + 1L

    TeddCard(padding = androidx.compose.foundation.layout.PaddingValues(16.dp)) {
        Text(
            text = "ÉCHÉANCE PASSÉE · ${TeddDates.formatShort(deadline) ?: deadline} · TENTATIVE N°$attempt",
            style = TeddType.Overline,
            color = TeddTokens.InkMuted,
        )
        Text(
            text = "Qu'as-tu appris de cette tentative qui servira demain ?",
            style = TeddType.BodyLg,
            color = TeddTokens.Ink,
            modifier = Modifier.padding(top = 8.dp, bottom = 10.dp),
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 72.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .padding(12.dp),
        ) {
            BasicTextField(
                value = text,
                onValueChange = { text = it },
                textStyle = TeddType.BodyLg.copy(color = TeddTokens.Ink, fontSize = 14.sp, lineHeight = 21.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                modifier = Modifier.fillMaxWidth(),
                decorationBox = { inner ->
                    if (text.isEmpty()) {
                        Text("Ce que la tentative t'a appris, en une ou deux phrases…", color = TeddTokens.InkSubtle, style = TeddType.BodyLg, fontSize = 14.sp)
                    }
                    inner()
                },
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            TeddButton(
                text = "Enregistrer la réflexion",
                onClick = {
                    val t = text.trim()
                    if (t.isNotEmpty()) {
                        scope.launch { repos.reflections.record(objective.id, t, deadline) }
                        core?.record(TeddIdentity.USER_ID, CoreFacts.growthReflection(objective.id, t))
                        text = ""
                    }
                },
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Sm,
                enabled = text.isNotBlank(),
            )
            TeddButton(
                text = if (pickingDeadline) "Garder sans échéance" else "Nouvelle échéance",
                onClick = {
                    if (pickingDeadline) {
                        scope.launch { repos.setObjectiveDeadline(objective, null) }
                        pickingDeadline = false
                    } else {
                        pickingDeadline = true
                    }
                },
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
            )
        }
        if (pickingDeadline) {
            TeddDateField(
                label = null,
                value = "",
                onValueChange = { picked ->
                    if (picked.isNotBlank()) {
                        scope.launch { repos.setObjectiveDeadline(objective, picked) }
                        pickingDeadline = false
                    }
                },
                placeholder = "Choisir la nouvelle échéance",
                minIso = TeddDates.todayIso,
                clearable = false,
                modifier = Modifier.padding(top = 10.dp),
            )
        }
        if (reflections.isNotEmpty()) {
            Column(modifier = Modifier.padding(top = 12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                reflections.sortedByDescending { it.attempt }.forEach { r ->
                    Column {
                        Text("Tentative n°${r.attempt}", style = TeddType.Overline, color = TeddTokens.InkMuted)
                        Text("« ${r.text} »", style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 14.sp, lineHeight = 21.sp)
                    }
                }
            }
        }
        Text(
            text = "Aucune pénalité. L'objectif reste ouvert.",
            color = TeddTokens.InkMuted,
            fontSize = 11.5.sp,
            modifier = Modifier.padding(top = 10.dp),
        )
    }
}
